import React, { useState } from "react";
import { Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useGoogleTasksSourceSettingsText,
  useGoogleTasksSourceSettingsViewModel,
} from "@onetasklist/shared/sources/googletasks";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface GoogleTasksSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const GoogleTasksSourceSettings: React.FC<
  GoogleTasksSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useGoogleTasksSourceSettingsText(t);
  const vm = useGoogleTasksSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [listsQuery, setListsQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  if (vm.hasError) {
    return <Text>{text.loadErrorText}</Text>;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.googletasks.sectionLists",
      )}
    >
      <Field
        orientation="horizontal"
        label={text.listsLabelText}
        hint={text.listsHintText}
      >
        <SourceMultiTagPicker
          selectedIds={vm.selectedListIds}
          query={listsQuery}
          placeholder={text.listsPlaceholderText}
          options={vm.lists.map((list) => ({
            id: list.id,
            label: list.title ?? list.id,
          }))}
          onQueryChange={setListsQuery}
          onSelectionChange={vm.onSelectLists}
          minQueryLength={0}
          typeToSearchText={text.listsHintText}
          isLoading={!vm.listsLoaded}
        />
      </Field>
    </SettingsSectionCard>
  );
};
